<template>
  <div>
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-qup-blue to-qup-dark text-white overflow-hidden">
      <div class="absolute inset-0 bg-black opacity-20"></div>
      <div class="container-max section-padding relative z-10">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="animate-fade-in">
            <h1 class="text-4xl md:text-6xl font-bold mb-6">
              Transform Your Business with
              <span class="text-yellow-300">Innovative Technology</span>
            </h1>
            <p class="text-xl mb-8 text-gray-200">
              Qup Teknoloji provides expert IT Risk & Security Consultancy, Process Optimization, and Software Development services to help your business thrive in the digital age.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <router-link to="/services" class="btn-primary text-center">
                Explore Our Services
              </router-link>
              <router-link to="/contact" class="btn-secondary text-center">
                Get in Touch
              </router-link>
            </div>
          </div>
          
          <div class="flex justify-center lg:justify-end">
            <img src="/Qup.svg" alt="Qup Teknoloji Logo" class="h-64 w-64 lg:h-80 lg:w-80 animate-pulse" />
          </div>
        </div>
      </div>
    </section>

    <!-- Services Overview -->
    <section class="section-padding bg-gray-50">
      <div class="container-max">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-qup-dark mb-4">
            Our Core Services
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            We specialize in three key areas that drive business transformation and digital innovation.
          </p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div 
            v-for="(service, index) in services" 
            :key="service.title"
            class="bg-white rounded-lg shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 animate-slide-up"
            :style="{ animationDelay: `${index * 0.2}s` }"
          >
            <div class="text-4xl mb-4">{{ service.icon }}</div>
            <h3 class="text-xl font-semibold text-qup-dark mb-4">{{ service.title }}</h3>
            <p class="text-gray-600 mb-6">{{ service.description }}</p>
            <ul class="space-y-2 text-sm text-gray-600">
              <li v-for="feature in service.features" :key="feature" class="flex items-center">
                <svg class="w-4 h-4 text-qup-blue mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
                {{ feature }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us -->
    <section class="section-padding">
      <div class="container-max">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl md:text-4xl font-bold text-qup-dark mb-6">
              Why Choose Qup Teknoloji?
            </h2>
            <div class="space-y-6">
              <div v-for="(reason, index) in reasons" :key="reason.title" class="flex items-start space-x-4">
                <div class="flex-shrink-0 w-12 h-12 bg-qup-blue rounded-lg flex items-center justify-center">
                  <span class="text-white font-bold">{{ index + 1 }}</span>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-qup-dark mb-2">{{ reason.title }}</h3>
                  <p class="text-gray-600">{{ reason.description }}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div class="bg-gradient-to-br from-qup-blue to-qup-dark rounded-lg p-8 text-white">
            <h3 class="text-2xl font-bold mb-6">Ready to Transform Your Business?</h3>
            <p class="mb-6 text-gray-200">
              Let's discuss how our expertise in IT Risk & Security, Process Optimization, and Software Development can help you achieve your business goals.
            </p>
            <router-link to="/contact" class="btn-secondary inline-block">
              Start Your Journey
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section-padding bg-qup-dark text-white">
      <div class="container-max text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          Ready to Get Started?
        </h2>
        <p class="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
          Contact us today to learn how Qup Teknoloji can help transform your business with our expert technology solutions and consulting services.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <router-link to="/contact" class="btn-primary">
            Contact Us Now
          </router-link>
          <router-link to="/services" class="btn-secondary">
            Learn More
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">

const services = [
  {
    icon: '🔒',
    title: 'IT Risk & Security Consultancy',
    description: 'Comprehensive security assessments and risk management strategies to protect your digital assets.',
    features: [
      'Security Audits & Assessments',
      'Risk Management Frameworks',
      'Compliance & Governance',
      'Incident Response Planning'
    ]
  },
  {
    icon: '⚡',
    title: 'Process Optimization',
    description: 'Streamline your business operations with data-driven process improvement and automation.',
    features: [
      'Business Process Analysis',
      'Workflow Automation',
      'Performance Optimization',
      'Digital Transformation'
    ]
  },
  {
    icon: '💻',
    title: 'Software Development & Integration',
    description: 'Custom software solutions and seamless system integration to meet your unique business needs.',
    features: [
      'Custom Software Development',
      'System Integration',
      'API Development',
      'Legacy System Modernization'
    ]
  }
]

const reasons = [
  {
    title: 'Expert Team',
    description: 'Our experienced professionals bring deep expertise in technology consulting and implementation.'
  },
  {
    title: 'Proven Results',
    description: 'We have successfully delivered transformative solutions for businesses across various industries.'
  },
  {
    title: 'Tailored Approach',
    description: 'Every solution is customized to meet your specific business requirements and objectives.'
  },
  {
    title: 'Ongoing Support',
    description: 'We provide continuous support and maintenance to ensure your solutions remain effective.'
  }
]
</script>

